<template>
  <div class="col">

    <div
      class="card h-100 shadow-sm book-card"
      :class="
        book.pages < 50
          ? 'less'
          : 'more'
      "
    >

      <!-- Image -->
      <div class="image-wrapper">

        <img
          :src="book.image"
          :alt="book.name"
          class="card-img-top book-img"
        >

        <span class="category-badge">
          {{ book.category }}
        </span>

      </div>

      <!-- Content -->
      <div
        class="card-body d-flex flex-column"
      >

        <h5
          class="card-title text-center fw-bold book-title"
        >
          {{ book.name }}
        </h5>

        <p
          class="text-center mb-3 book-author"
        >
          by {{ book.author }}
        </p>

        <!-- Book Information -->
        <div class="book-info small mb-3">

          <div class="info-item">

            <span class="info-label">
              📖 Pages
            </span>

            <strong class="info-value">
              {{ book.pages }}
            </strong>

          </div>

          <div class="info-item">

            <span class="info-label">
              💰 Price
            </span>

            <strong class="info-value">
              {{ formatPrice(book.price) }}
            </strong>

          </div>

          <div class="info-item">

            <span class="info-label">
              🔖 ISBN
            </span>

            <strong class="info-value isbn-value">
              {{ book.ISBN }}
            </strong>

          </div>

        </div>

        <div class="mt-auto">

          <!-- Details -->
          <router-link
            :to="
              '/books/' +
              book.ISBN
            "
            class="btn btn-info text-white w-100 mb-2"
          >
            View Details
          </router-link>

          <!-- Wishlist -->
          <button
            v-if="isBuyer"
            class="btn w-100 mb-2"
            :class="
              isInWishlist(book)
                ? 'btn-secondary'
                : 'btn-outline-primary'
            "
            :disabled="
              isInWishlist(book)
            "
            @click="addToWishlist"
          >
            {{
              isInWishlist(book)
                ? '✓ Added to Wishlist'
                : '♡ Add to Wishlist'
            }}
          </button>

          <!-- Cart -->
          <button
            v-if="isBuyer"
            class="btn w-100 mb-2"
            :class="
              isInCart(book)
                ? 'btn-success'
                : 'btn-primary'
            "
            @click="addToCart"
          >
            {{
              isInCart(book)
                ? '🛒 Added to Cart'
                : '🛒 Add to Cart'
            }}
          </button>

          <!-- Cart Quantity -->
          <div
            v-if="
              isBuyer &&
              isInCart(book)
            "
            class="quantity-info"
          >
            <span class="quantity-number">
              {{ cartQuantity(book) }}
            </span>

            <span>
              in your cart
            </span>
          </div>

          <!-- Seller Actions -->
          <div
            v-if="isSeller"
            class="d-flex gap-2"
          >

            <router-link
              :to="
                '/books/edit/' +
                book.id
              "
              class="btn btn-outline-warning w-50"
            >
              ✏️ Edit
            </router-link>

            <button
              class="btn btn-outline-danger w-50"
              @click="handleDelete"
            >
              🗑️ Delete
            </button>

          </div>

        </div>

      </div>

    </div>

  </div>
</template>

<script>
export default {
  name: 'BookCard',

  props: {
    book: {
      type: Object,
      required: true
    },

    wishlist: {
      type: Array,
      default() {
        return []
      }
    }
  },

  computed: {
    isBuyer() {
      return this.$store
        .getters.isBuyer
    },

    isSeller() {
      return this.$store
        .getters.isSeller
    }
  },

  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat(
        'en-SA',
        {
          style: 'currency',
          currency: 'SAR'
        }
      ).format(value)
    },

    isInWishlist(book) {
      return this.wishlist.some(
        item =>
          item.ISBN === book.ISBN
      )
    },

    addToWishlist() {
      this.$store.dispatch(
        'addToWishlist',
        this.book
      )
    },

    isInCart(book) {
      return this.$store.getters
        .isInCart(book.ISBN)
    },

    cartQuantity(book) {
      return this.$store.getters
        .cartItemQuantity(
          book.ISBN
        )
    },

    addToCart() {
      this.$store.dispatch(
        'addToCart',
        this.book
      )
    },

    async handleDelete() {
      const confirmed =
        window.confirm(
          `Are you sure you want to delete "${this.book.name}"?`
        )

      if (!confirmed) {
        return
      }

      const result =
        await this.$store.dispatch(
          'deleteBook',
          this.book.id
        )

      if (!result.success) {
        alert(
          result.message
        )
      }
    }
  }
}
</script>
<style scoped>
.book-card {
  border: none;

  border-radius: 20px;

  overflow: hidden;

  background:
    var(--surface);

  box-shadow:
    0 8px 28px
    rgba(108, 59, 255, 0.08);

  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.book-card:hover {
  transform:
    translateY(-7px);

  box-shadow:
    0 18px 38px
    rgba(108, 59, 255, 0.15);
}

.card.less {
  border-top:
    4px solid #ec4899;
}

.card.more {
  border-top:
    4px solid #7c3aed;
}

.image-wrapper {
  position:
    relative;

  overflow:
    hidden;
}

.book-img {
  width: 100%;
  height: 300px;
  object-fit: contain;
  background: var(--surface);
}

.book-card:hover .book-img {
  transform: scale(1.05);
}

.category-badge {
  position:
    absolute;

  top:
    12px;

  left:
    12px;

  padding:
    6px 11px;

  border-radius:
    50px;

  background:
    rgba(
      255,
      255,
      255,
      0.94
    );

  color:
    var(--primary);

  font-size:
    0.73rem;

  font-weight:
    700;

  box-shadow:
    0 3px 10px
    rgba(0, 0, 0, 0.08);
}

.book-title {
  color:
    var(--text);
}

.book-author {
  color:
    var(--text-secondary);
}

.book-info {
  padding:
    10px 0;

  border-top:
    1px solid var(--border);

  border-bottom:
    1px solid var(--border);
}

.info-item {
  display:
    flex;

  justify-content:
    space-between;

  align-items:
    center;

  gap:
    15px;

  padding:
    8px 0;
}

.info-label {
  color:
    var(--text-secondary);
}

.info-value {
  color:
    var(--text);
}

.quantity-info {
  display:
    flex;

  justify-content:
    center;

  gap:
    6px;

  margin-bottom:
    9px;

  color:
    var(--success);

  font-size:
    0.8rem;

  font-weight:
    600;
}

.quantity-number {
  display:
    inline-flex;

  justify-content:
    center;

  align-items:
    center;

  min-width:
    25px;

  height:
    25px;

  border-radius:
    50%;

  background:
    #e4fff4;

  color:
    #047857;

  font-weight:
    700;
}

.btn {
  border-radius:
    9px;

  font-weight:
    600;
}

:global(.dark-mode)
.category-badge {
  background:
    rgba(
      31,
      22,
      51,
      0.94
    );

  color:
    #c4b5fd;

  border:
    1px solid #4a3b61;
}

:global(.dark-mode)
.quantity-number {
  background:
    #19372d;

  color:
    #8ee7bb;
}

:global(.dark-mode)
.quantity-info {
  color:
    #7ee2ae;
}
</style>